import { z, type ZodRawShape } from "zod";
import type { ZodOpenApiPathsObject } from "zod-openapi";
import { extendZodWithOpenApi } from "zod-openapi";
import { formBody, ind, jsonBody, successResponse } from "../util.ts";
import {
    billing_account_id,
    created_at,
    datetime,
    deleted_at,
    form_billing_account_id,
    int,
    ip,
    ipv4,
    network_uuid,
    updated_at,
    user_id,
    uuid,
} from "./common.ts";
import * as params from "./params.ts";

extendZodWithOpenApi(z);

//#region IP Address

const IpAddressResourceType = z
    .enum(["virtual_machine", "service", "load_balancer"])
    .openapi({ ref: "IpAddressResourceType" });

const IpAddressFields = {
    id: int.describe("IP address ID"),
    address: ip,
    user_id,
    // TODO: Backend has a bug that erases this field when updating
    billing_account_id: billing_account_id.optional(),
    type: z.enum(["public", "private"]).openapi({ ref: "IpAddressType" }),
    enabled: z.boolean(),
    created_at,
    updated_at: updated_at.optional(),
    uuid,
    is_deleted: z.boolean(),
    is_ipv6: z.boolean(),
    name: z.string().optional(),
    assigned_to: z
        .string()
        .optional()
        .describe("ID of the resource this IP is assigned to (usually UUID or integer-as-string)"),
    assigned_to_resource_type: IpAddressResourceType.optional(),
    assigned_to_private_ip: ipv4.optional().describe("Private IP address on the current assignment target"),
    unassigned_at: datetime.optional().describe("Latest time the floating IP was detached from a resource"),
    deleted_at: deleted_at.nullable().optional(),
} as const satisfies ZodRawShape;

export const IpAddress = z.object(IpAddressFields).openapi({ ref: "IpAddress" });

export const IpAddressCreateBody = z
    .object({
        billing_account_id: form_billing_account_id,
        name: IpAddressFields.name,
    })
    .openapi({ ref: "IpAddressCreateBody" });

export const IpAddressModifyBody = z
    .object({
        billing_account_id: form_billing_account_id,
        name: IpAddressFields.name,
    })
    .openapi({ ref: "IpAddressModifyBody" });

export const IpAddressAssignBody = z
    .object({
        assigned_to_resource_type: IpAddressFields.assigned_to_resource_type,
        assigned_to: IpAddressFields.assigned_to,
    })
    .openapi({ ref: "IpAddressAssignBody" });

//#endregion

//#region VPC Network

const ipv6_subnet = z.string().regex(/^[0-9a-fA-F:]+\/\d+$/);

const VpcFields = {
    id: int.optional(),
    name: z.string(),
    user_id: user_id.optional(),
    billing_account_id: billing_account_id.optional(),
    created_at,
    updated_at: created_at.optional(),
    uuid,
    type: z.enum(["public", "private"]).openapi({ ref: "VpcType" }),
    vm_uuids: z.array(uuid),
    resources_count: int,
    subnet_ipv6: z.union([z.literal(""), ipv6_subnet]),
    is_default: z.boolean(),
    subnet: z
        .string()
        .cidr({ version: "v4" })
        .describe("Subnet assigned to VPC, CIDR formatted")
        .openapi({ example: "172.16.10.0/24" }),
} as const satisfies ZodRawShape;

export const Vpc = z
    .object({
        ...VpcFields,
        name: VpcFields.name.nullable(),
    })
    .openapi({ ref: "Vpc" });

export const VpcCreateBody = z
    .object({
        name: VpcFields.name,
        subnet: VpcFields.subnet.optional(),
    })
    .openapi({ ref: "VpcCreateBody" });

export const VpcUpdateBody = z
    .object({
        name: VpcFields.name,
    })
    .openapi({ ref: "VpcUpdateBody" });

//#endregion

//#region Load Balancer
const LbSessionPersistence = z.enum(["SOURCE_IP"]).openapi({ ref: "LbSessionPersistence" });

export const LbForwardingRule = z
    .object({
        protocol: z.enum(["TCP"]).openapi({ ref: "LbProtocol" }),
        uuid,
        created_at,
        source_port: int,
        target_port: int,
        settings: z.object({
            connection_limit: int,
            session_persistence: LbSessionPersistence,
        }),
    })
    .openapi({ ref: "LbForwardingRule" });

const LbTargetType = z.enum(["vm"]).openapi({ ref: "LbTargetType" });

export const LbTarget = z
    .object({
        created_at,
        target_uuid: uuid.describe("Load balancer target resource UUID"),
        target_type: LbTargetType,
        target_ip_address: z.string().ip().optional(),
    })
    .openapi({ ref: "LbTarget" });

const LoadBalancerFields = {
    uuid,
    display_name: z.string(),
    user_id,
    billing_account_id,
    created_at,
    updated_at: updated_at.optional(),
    deleted_at: deleted_at.nullable(),
    is_deleted: z.boolean(),
    private_address: z.string().ip(),
    network_uuid,
    forwarding_rules: z.array(LbForwardingRule),
    targets: z.array(LbTarget),
} as const satisfies ZodRawShape;

export const LoadBalancer = z.object(LoadBalancerFields).openapi({ ref: "LoadBalancer" });

export const LbCreateForwardingRule = z
    .object({
        source_port: LbForwardingRule.shape.source_port,
        target_port: LbForwardingRule.shape.target_port,
    })
    .openapi({ ref: "LbCreateForwardingRule" });

export const LbCreateTarget = z
    .object({
        target_type: LbTarget.shape.target_type,
        target_uuid: LbTarget.shape.target_uuid,
    })
    .openapi({ ref: "LbCreateTarget" });

export const LoadBalancerCreateBody = z
    .object({
        display_name: LoadBalancerFields.display_name,
        billing_account_id: LoadBalancerFields.billing_account_id,
        network_uuid: LoadBalancerFields.network_uuid,
        rules: z.array(LbCreateForwardingRule),
        targets: z.array(LbCreateTarget),
        // XXX: Wrong as there is no need for created_at and target_ip_address on creating LB
        // targets: LoadBalancerFields.targets,
        reserve_public_ip: z.boolean(),
    })
    .openapi({ ref: "LoadBalancerCreateBody" });

export const LoadBalancerRenameBody = z
    .object({
        display_name: LoadBalancerFields.display_name,
    })
    .openapi({ ref: "LoadBalancerRenameBody" });

const set_id_param = z
    .string()
    .describe("Billing account ID to set")
    .openapi({ param: { name: "set_id", in: "query" } });

export const address_path = z.string().openapi({ param: { name: "address", in: "path", ref: "address" } });

//#endregion

//#region Firewall
const FirewallAssignedResourceType = z.enum(["vm"]).openapi({ ref: "FirewallAssignedResourceType" });
const FirewallRuleDirectionType = z.enum(["inbound", "outbound"]).openapi({ ref: "FirewallRuleDirectionType" });
const FirewallRuleProtocolType = z
    .enum(["tcp", "udp", "icmp", "icmp6", "any"])
    .openapi({ ref: "FirewallRuleProtocolType" });
const FirewallRuleEndpointSpecType = z.enum(["ip_prefixes", "any"]).openapi({
    ref: "FirewallRuleEndpointSpecType",
});

export const FirewallAssignedResource = z
    .object({
        resource_uuid: uuid,
        resource_type: FirewallAssignedResourceType,
    })
    .openapi({ ref: "FirewallAssignedResource" });

// XXX: min should be 1
const port = int.min(0).max(65535);

export const FirewallRule = z
    .object({
        uuid: uuid.optional(),
        direction: FirewallRuleDirectionType,
        protocol: FirewallRuleProtocolType,
        policy_action: z.literal("pass").optional(),
        port_start: port.nullable().optional().describe("`0` for eg. ICMP protocol (legacy value)"),
        port_end: port.nullable().optional().describe("`null` implies that it is equal to `port_start`"),
        endpoint_spec_type: FirewallRuleEndpointSpecType,
        endpoint_spec: z
            .array(z.string())
            .openapi({ example: ["10.0.0.0/24", "192.168.1.0/24"] })
            .nullable()
            .optional()
            .describe(
                'If `endpoint_spec_type` is "ip_prefixes", this must be a valid array of IP addresses or ranges (CIDR notation)',
            ),
    })
    .openapi({ ref: "FirewallRule" });

const FirewallFields = {
    uuid: uuid,
    display_name: z.string(),
    created_at,
    user_id,
    billing_account_id, // XXX: is this correct - should it be not optional
    rules: z.array(FirewallRule),
    resources_assigned: z.array(FirewallAssignedResource),
    tf_pass_in_policy_uuid: uuid.optional(),
    tf_pass_out_policy_uuid: uuid.optional(),
} as const satisfies ZodRawShape;

export const Firewall = z
    .object({
        ...FirewallFields,
        deleted_at: deleted_at.nullable().optional(),
    })
    .openapi({ ref: "Firewall" });

export const FirewallCreateBody = z
    .object({
        billing_account_id: FirewallFields.billing_account_id.optional(),
        display_name: FirewallFields.display_name,
        rules: FirewallFields.rules,
        resources_assigned: FirewallFields.resources_assigned.optional(),
    })
    .openapi({ ref: "FirewallCreateBody" });

export const FirewallModifyBody = z
    .object({
        billing_account_id: FirewallFields.billing_account_id.optional(),
        display_name: FirewallFields.display_name.optional(),
        rules: FirewallFields.rules.optional(),
    })
    .openapi({ ref: "FirewallModifyBody" });

//#endregion

export const VpcListResponse = z.array(Vpc);
export const LoadBalancerListResponse = z.array(LoadBalancer);
export const IpListResponse = z.array(IpAddress);
export const FirewallResponse = z.array(Firewall);

const vm_uuid_query = uuid.openapi({ param: { name: "vm_uuid", in: "query" } });

export const networkPaths: ZodOpenApiPathsObject = {
    "/{location}/network/ip_addresses": {
        get: {
            tags: ["ip_address"],
            summary: "List floating IPs",
            // XXX: This is missing these query parameter declarations!
            // description: ind`"List floating IPs. Can be filtered by \`billing_account_id\` and \`vm_uuid\`, if necessary.`,
            parameters: [params.location],
            responses: {
                ...successResponse(IpListResponse),
            },
        },
        post: {
            summary: "Create floating IP",
            tags: ["ip_address"],
            requestBody: jsonBody(IpAddressCreateBody),
            parameters: [params.location],
            responses: {
                ...successResponse(IpAddress),
            },
        },
    },
    "/{location}/network/ip_addresses/{address}": {
        patch: {
            summary: "Update floating IP",
            tags: ["ip_address"],
            requestBody: jsonBody(IpAddressModifyBody),
            parameters: [params.location, address_path],
            responses: {
                ...successResponse(IpAddress),
            },
        },
        delete: {
            summary: "Delete floating IP",
            tags: ["ip_address"],
            parameters: [params.location, address_path],
            responses: {
                ...successResponse(z.boolean()),
            },
        },
    },

    "/{location}/network/ip_addresses/{address}/assign": {
        post: {
            summary: "Assign floating IP",
            description: ind`
                Floating IP can be assigned to resources of the following types:
                - Virtual Machine
                - Managed Service
                - Load Balancer
            `,
            tags: ["ip_address"],
            requestBody: formBody(IpAddressAssignBody),
            parameters: [params.location, address_path],
            responses: {
                ...successResponse(IpAddress),
            },
        },
    },
    "/{location}/network/ip_addresses/{address}/unassign": {
        post: {
            summary: "Unassign floating IP",
            tags: ["ip_address"],
            parameters: [params.location, address_path],
            responses: {
                ...successResponse(IpAddress),
            },
        },
    },
    "/{location}/network/networks": {
        get: {
            tags: ["vpc"],
            summary: "List networks",
            description: "List user networks with resources.",
            parameters: [params.location],
            responses: {
                ...successResponse(VpcListResponse),
            },
        },
    },
    "/{location}/network/network/default": {
        post: {
            summary: "Create or retrieve the default network",
            description:
                "Returns the default network if one already exists. Otherwise, creates a new network, flagging it as default.",
            tags: ["vpc"],
            parameters: [params.location],
            responses: {
                ...successResponse(Vpc),
            },
        },
    },
    "/{location}/network/network": {
        post: {
            summary: "Create network",
            description: ind`
                Creates a new private network. When the \`subnet\` parameter is not provided, an arbitrary /24 size
                network will be assigned from range \`10.*.*.0\`, e.g.: \`10.20.30.0/24\`.

                When \`subnet\` is provided it must specify a network in CIDR notation with prefix length not shorter
                than /16 and not longer than /29. The subnet must fall within one the following prefix blocks:
                 - blocks \`10.0.*.*/16\` to \`10.255.*.*/16\`
                 - blocks \`172.16.*.*/16\` to \`172.31.*.*/16\`
                 - \`192.168.*.*/16\`
                 - blocks \`100.64.*.*/16\` to \`100.127.*.*/16\`

                If the created network is the first network of the user, it is marked as default.
            `,
            tags: ["vpc"],
            requestBody: formBody(VpcCreateBody),
            parameters: [params.location],
            responses: {
                ...successResponse(Vpc),
            },
        },
    },
    "/{location}/network/network/{uuid}": {
        patch: {
            summary: "Change network name",
            tags: ["vpc"],
            requestBody: jsonBody(VpcUpdateBody),
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(z.unknown()),
            },
        },
        delete: {
            summary: "Delete network",
            description:
                "Delete network. The network can be deleted if it does not contain any resources and it is not default.",
            tags: ["vpc"],
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(z.unknown()),
            },
        },
    },
    "/{location}/network/network/{uuid}/default": {
        put: {
            summary: "Set network as default",
            description:
                "Change network to default. The network is change to the default network for the future resources. The other private networks will be changed to non default.",
            tags: ["vpc"],
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(Vpc),
            },
        },
    },
    "/{location}/network/load_balancers": {
        get: {
            tags: ["load_balancer"],
            summary: "List load balancers",
            description: "Get list of load balancers owned by user.",
            parameters: [params.location],
            responses: {
                ...successResponse(LoadBalancerListResponse),
            },
        },
        post: {
            summary: "Create load balancer",
            tags: ["load_balancer"],
            requestBody: jsonBody(LoadBalancerCreateBody),
            parameters: [params.location],
            responses: {
                ...successResponse(LoadBalancer),
            },
        },
    },
    "/{location}/network/load_balancers/{uuid}": {
        patch: {
            summary: "Rename load balancer",
            tags: ["load_balancer"],
            requestBody: jsonBody(LoadBalancerRenameBody),
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(LoadBalancer),
            },
        },
        delete: {
            summary: "Delete load balancer",
            tags: ["load_balancer"],
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(z.unknown()),
            },
        },
    },
    "/{location}/network/load_balancers/{uuid}/billing_account": {
        put: {
            summary: "Change billing account",
            description: "Change Billing Account that load balancer is assigned to.",
            tags: ["load_balancer"],
            parameters: [params.location, params.uuid, set_id_param],
            responses: {
                ...successResponse(LoadBalancer),
            },
        },
    },

    "/{location}/network/load_balancers/{uuid}/targets": {
        post: {
            summary: "Add target",
            description: "Add new target to load balancer",
            tags: ["load_balancer"],
            requestBody: jsonBody(LbCreateTarget),
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(LbTarget, "Success: Target added to load balancer"),
            },
        },
    },
    "/{location}/network/load_balancers/{uuid}/targets/{target_uuid}": {
        delete: {
            summary: "Unlink target",
            description: "Unlink target from load balancer",
            tags: ["load_balancer"],
            parameters: [params.location, params.uuid, params.target_uuid],
            responses: {
                ...successResponse(z.unknown()),
            },
        },
    },
    "/{location}/network/load_balancers/{uuid}/forwarding_rules": {
        post: {
            summary: "Add forwarding rule",
            description: "Add new port forwarding rule to load balancer",
            tags: ["load_balancer"],
            requestBody: jsonBody(LbCreateForwardingRule),
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(LbForwardingRule),
            },
        },
    },
    "/{location}/network/load_balancers/{uuid}/forwarding_rules/{rule_uuid}": {
        delete: {
            summary: "Delete forwarding rule",
            description: "Drop port forwarding rule from load balancer",
            tags: ["load_balancer"],
            parameters: [params.location, params.uuid, params.rule_uuid],
            responses: {
                ...successResponse(z.unknown()),
            },
        },
    },

    "/{location}/network/firewalls": {
        get: {
            tags: ["firewall"],
            summary: "List firewalls",
            description: "Retrieves a list of firewalls for the current user.",
            parameters: [params.location],
            responses: {
                ...successResponse(FirewallResponse),
            },
        },
        post: {
            summary: "Create firewall",
            description: "Creates a new firewall with a given set of rules.",
            tags: ["firewall"],
            requestBody: jsonBody(FirewallCreateBody),
            parameters: [params.location],
            responses: {
                ...successResponse(Firewall),
            },
        },
    },
    "/{location}/network/firewalls/{uuid}": {
        put: {
            summary: "Update firewall",
            description: "Updates an existing firewall configuration.",
            tags: ["firewall"],
            requestBody: jsonBody(FirewallModifyBody),
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(Firewall),
            },
        },
        delete: {
            summary: "Delete firewall",
            description: "Deletes a specified firewall.",
            tags: ["firewall"],
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(z.boolean()),
            },
        },
    },
    "/{location}/network/firewalls/{uuid}/vms": {
        post: {
            summary: "Attach firewall to VM",
            description: "Associates a firewall with a specific virtual machine.",
            tags: ["firewall"],
            parameters: [params.location, params.uuid, vm_uuid_query],
            responses: {
                ...successResponse(z.array(FirewallAssignedResource)),
            },
        },
        delete: {
            summary: "Detach firewall from VM",
            description: "Removes the association between a firewall and a virtual machine.",
            tags: ["firewall"],
            parameters: [params.location, params.uuid, vm_uuid_query],
            responses: {
                ...successResponse(z.boolean()),
            },
        },
    },
};
